import React from 'react';
import PropTypes from 'prop-types';
import CustomTooltip from '../../Utils/CustomTooltip';

// StrengthsConcernsCard component for displaying strengths and concerns
const StrengthsConcernsCard = ({ strengths, concerns, showSources = true }) => {
  // BACKWARDS COMPATIBILITY -> Helper function to get items array, checking both 'items' and 'value' properties
  const getItemsArray = (data) => {
    if (Array.isArray(data?.items) && data.items.length > 0) {
      return data.items;
    } else if (Array.isArray(data?.value) && data.value.length > 0) {
      return data.value;
    }
    return [];
  };
  
  // Get the items arrays for strengths and concerns
  const strengthsItems = getItemsArray(strengths);
  const concernsItems = getItemsArray(concerns);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Strengths */}
      <div className="border border-gray-200 rounded-lg p-6 bg-white relative">
        <div className="flex justify-between items-start mb-3">
          <CustomTooltip 
            content={strengths?.rationale || "No rationale provided"}
            position="right"
          >
            <h4 className="text-lg font-semibold text-gray-800 cursor-help">Strengths</h4>
          </CustomTooltip>
          {showSources && strengths?.source && (
            <CustomTooltip content={strengths.source} position="left">
              <span className="text-xs text-blue-600 cursor-help hover:text-blue-800 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Source
              </span>
            </CustomTooltip>
          )}
        </div>
        {strengthsItems.length > 0 ? (
          <ul className="space-y-2">
            {strengthsItems.map((strength, index) => (
              <li key={index} className="flex items-start">
                <span className="text-green-500 mr-2 text-xl">•</span>
                <span className="text-gray-800 text-lg">{strength}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-600 text-lg">No strengths data available.</p>
        )}
      </div>
      
      {/* Concerns */}
      <div className="border border-gray-200 rounded-lg p-6 bg-white relative">
        <div className="flex justify-between items-start mb-3">
          <CustomTooltip content={concerns?.rationale || "No rationale provided"}>
            <h4 className="text-lg font-semibold text-gray-800 cursor-help">Concerns</h4>
          </CustomTooltip>
          {showSources && concerns?.source && (
            <CustomTooltip content={concerns.source} position="left">
              <span className="text-xs text-blue-600 cursor-help hover:text-blue-800 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Source
              </span>
            </CustomTooltip>
          )}
        </div>
        {concernsItems.length > 0 ? (
          <ul className="space-y-2">
            {concernsItems.map((concern, index) => (
              <li key={index} className="flex items-start">
                <span className="text-red-500 mr-2 text-xl">•</span>
                <span className="text-gray-800 text-lg">{concern}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-600 text-lg">No concerns data available.</p>
        )}
      </div>
    </div>
  );
};

StrengthsConcernsCard.propTypes = {
  strengths: PropTypes.object,
  concerns: PropTypes.object,
  showSources: PropTypes.bool
};

export default StrengthsConcernsCard;
